<template>
  <div class="pptconvert">
    <!-- 顶部：返回 + 标题 -->
    <div class="topbar">
      <el-button icon="el-icon-back" @click="$router.push('/pptlist')">返回列表</el-button>
      <span class="page-title">PPT 转换</span>
      <span class="tip">两种模式：快速转换 = 整页渲染（转换速度快，适合无动画 PPT）；复杂转换 = 逐页自动分析（转换速度慢，动画页截取关键帧逐步骤播放、视频页自动提取，适合包含动画和音视频的ppt）</span>
    </div>

    <!-- 第一步：上传并转换 -->
    <div v-if="!projectId && !busy" class="panel">
      <div class="panel-title">① 上传 .pptx 文件</div>
      <div class="upload-row">
        <el-button icon="el-icon-upload2" @click="$refs.pptxInput.click()">
          选择 .pptx 文件
        </el-button>
        <span class="file-name">{{ fileName || '未选择文件（≤1000MB）' }}</span>
      </div>
      <div class="upload-row">
        <el-input v-model="title" placeholder="PPT 名称（缺省取文件名）" style="width: 320px" />
        <el-button type="success" :loading="converting" :disabled="!file || convertingQuick" icon="el-icon-video-play" @click="startConvert">
          开始转换（复杂）
        </el-button>
        <el-button :loading="convertingQuick" :disabled="!file || converting" icon="el-icon-zoom-in" @click="startQuickConvert">
          快速转换（无动画）
        </el-button>
      </div>
      <div v-if="buildInfo" class="build-info">
        后端进程：{{ buildInfo.processStart }} 启动 ｜ 代码构建：{{ buildInfo.classesBuild }}
        <el-tooltip content="转换结果由后端进程实时产出：若这里的构建时间早于预期，说明跑的是旧进程，请重启后端再转换" placement="bottom">
          <i class="el-icon-info" style="margin-left: 4px" />
        </el-tooltip>
      </div>
      <input ref="pptxInput" type="file" accept=".pptx" style="display: none" @change="onPptxChange" />
    </div>

    <!-- 转换/加载中 -->
    <div v-if="busy" class="panel converting">
      <i class="el-icon-loading"></i>
      <span>{{ convertingQuick ? '正在快速转换（整页渲染），请稍候…' : '正在处理（逐页分析转换 / 加载转换结果），页面较多时请稍候…' }}</span>
    </div>

    <!-- 第二步：逐页分类确认 -->
    <div v-if="projectId && !busy" class="panel result-panel">
      <div class="panel-title">② 逐页分类结果（共 {{ pages.length }} 页）
        <el-button type="text" icon="el-icon-refresh" style="margin-left: 8px" @click="reset">重新上传</el-button>
      </div>

      <div class="stat-row">
        <div class="stat"><span class="num">{{ statNormal }}</span><span class="label">普通页</span></div>
        <div class="stat warn"><span class="num">{{ statAnim }}</span><span class="label">动画页（关键帧）</span></div>
        <div class="stat ok"><span class="num">{{ statVideo }}</span><span class="label">视频页</span></div>
      </div>

      <el-table :data="pages" size="mini" border height="520" style="width: 100%">
        <el-table-column label="页" width="64" align="center">
          <template slot-scope="scope">{{ scope.row.id }}</template>
        </el-table-column>
        <el-table-column label="缩略图" width="150" align="center">
          <template slot-scope="scope">
            <img :src="scope.row.type === 'sequence' && scope.row.frames ? scope.row.frames[0] : scope.row.img" class="thumb" alt="" />
          </template>
        </el-table-column>
        <el-table-column label="类型" width="150" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.type === 'hybrid'" size="mini" type="primary">视频（自动提取）</el-tag>
            <el-tag v-else-if="scope.row.type === 'video'" size="mini" type="success">视频（已补录）</el-tag>
            <el-tag v-else-if="scope.row.type === 'sequence'" size="mini" type="warning">动画（关键帧）</el-tag>
            <el-tag v-else-if="scope.row.anim" size="mini" type="warning">动画页</el-tag>
            <el-tag v-if="scope.row.audioMedia" size="mini" type="success" style="margin-left:4px">音频</el-tag>
            <el-tag v-if="!scope.row.anim && scope.row.type !== 'hybrid' && scope.row.type !== 'video' && scope.row.type !== 'sequence' && !scope.row.audioMedia" size="mini" type="info">普通</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="说明" min-width="300">
          <template slot-scope="scope">
            <span v-if="scope.row.type === 'hybrid'">
              已自动提取内嵌 mp4（{{ fmtVideo(scope.row.videoUrl) }}），预览时在整页图原位点击播放
            </span>
            <span v-else-if="scope.row.type === 'video'">
              已补录动画视频，预览时整页视频播放（封面为整页图）
            </span>
            <span v-else-if="scope.row.type === 'sequence'">
              已自动截取 {{ (scope.row.frames || []).length - 1 }} 个动画步骤的关键帧，预览时点「下一页」逐步骤播放（与 PowerPoint 放映一致）
            </span>
            <span v-else-if="scope.row.anim">
              本页含动画/切换效果，但截帧未启用或失败，按静态整页保底。可在右侧上传该页视频作为替代
            </span>
            <span v-else>普通页面，整页渲染保真</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="280" align="center">
          <template slot-scope="scope">
            <el-button
              v-if="scope.row.type === 'sequence' && scope.row.frames && scope.row.frames.length >= 2"
              size="mini" type="info" plain
              @click="previewAnim(scope.row)"
            >预览动画</el-button>
            <el-button
              v-if="!scope.row.anim && scope.row.type !== 'video' && scope.row.type !== 'hybrid' && scope.row.type !== 'sequence'"
              size="mini" plain
              :loading="imgUploadingId === scope.row.id"
              @click="openImgUpload(scope.row)"
            >上传图片</el-button>
            <el-button
              v-if="(scope.row.anim || scope.row.type === 'sequence') && scope.row.type !== 'video'"
              size="mini" type="warning" plain
              :loading="uploadingIdx === scope.row.id"
              @click="openVideoUpload(scope.row)"
            >上传视频</el-button>
            <el-button
              v-else-if="scope.row.type === 'video'"
              size="mini" type="danger" plain
              @click="clearPageVideo(scope.row)"
            >移除视频</el-button>
            <el-button
              size="mini" type="danger" plain
              style="margin-left: 8px"
              @click="delPage(scope.$index)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="save-row">
        <el-button type="primary" :loading="saving" icon="el-icon-check" @click="save">
          保存并进入预览
        </el-button>
        <span class="save-tip">删除 / 上传图片 / 上传视频等修改，点「保存」后统一生效；保存后进入检查页，确认无误即可导出 HTML 手动发布到 eOnline</span>
      </div>
      <input ref="videoInput" type="file" accept="video/mp4,.mp4" style="display: none" @change="onVideoChange" />
      <input ref="imgInput" type="file" accept=".jpg,.jpeg,.png" style="display: none" @change="onImgChange" />
    </div>

    <!-- 动画预览：判断该页动画效果是否满意，再决定是否用视频替代 -->
    <el-dialog title="动画效果预览" :visible.sync="animVisible" width="860px" top="4vh" @closed="stopAnimAuto">
      <div class="anim-pv">
        <div class="anim-stage">
          <img :src="animFrameSrc" alt="动画帧">
        </div>
        <div class="anim-ctrl">
          <el-button size="small" :disabled="animStep <= 0" @click="animPrev">‹ 上一步</el-button>
          <span class="anim-progress">第 {{ animStep }} 步 / 共 {{ animTotal }} 步动画（与 PowerPoint 放映一致，点「下一步」播下一帧）。若关键帧效果不满意，可关闭预览后「上传视频」用整页视频替代</span>
          <el-button size="small" :disabled="animStep >= animTotal" @click="animNext">下一步 ›</el-button>
          <el-button size="small" type="primary" :icon="animPlaying ? 'el-icon-video-pause' : 'el-icon-video-play'" @click="toggleAnimAuto">
            {{ animPlaying ? '暂停' : '自动播放' }}
          </el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { convertPptxSmart, convertPptx, uploadPptImage, uploadPptVideo, updatePptProject, getPptProjectById, getPptBuildInfo } from '@/api/ppt'

export default {
  name: 'pptconvert',
  data() {
    return {
      file: null,
      fileName: '',
      title: '',
      converting: false,
      convertingQuick: false,
      saving: false,
      uploadingIdx: null,
      projectId: null,
      pages: [],
      pageW: 960,
      pageH: 540,
      uploadTarget: null, // 正在上传视频的页对象
      imgTarget: null, // 正在上传替换图片的页对象
      imgUploadingId: null, // 正在上传图片的页号（按钮转圈）
      // 动画预览
      animVisible: false,
      animPreviewRow: null,
      animStep: 0,
      animPlaying: false,
      animTimer: null,
      // 后端构建/进程信息（识别旧进程）
      buildInfo: null
    }
  },
  created() {
    // 支持带 ?id= 进入：已转换草稿 → 直接显示逐页确认（转换后刷新/再次进入）
    const id = this.$route.query && this.$route.query.id
    if (id) {
      this.loadProject(id)
    }
    this.loadBuildInfo()
  },
  computed: {
    // 任一转换进行中：隐藏上传面板、显示进度面板，并互斥禁用两个按钮
    busy() {
      return this.converting || this.convertingQuick
    },
    statNormal() {
      return this.pages.filter((p) => !p.anim && p.type !== 'video' && p.type !== 'hybrid').length
    },
    statAnim() {
      return this.pages.filter((p) => p.anim && p.type !== 'video').length
    },
    statVideo() {
      return this.pages.filter((p) => p.type === 'hybrid' || p.type === 'video').length
    },
    // 动画预览帧（基于当前预览行）
    animFrames() {
      const p = this.animPreviewRow
      return (p && p.type === 'sequence' && Array.isArray(p.frames) && p.frames.length) ? p.frames : []
    },
    animTotal() {
      return Math.max(0, this.animFrames.length - 1)
    },
    animFrameSrc() {
      return this.animFrames[this.animStep] || ''
    }
  },
  methods: {
    // 后端构建/进程时间：转换页常驻显示，跑的是旧进程一眼可见
    loadBuildInfo() {
      getPptBuildInfo()
        .then((res) => {
          const d = (res && res.data && res.data.data) || null
          if (d) this.buildInfo = d
        })
        .catch(() => {})
    },
    // 加载已转换的草稿项目（列表导入 / 再次进入本页）
    loadProject(id) {
      this.converting = true
      getPptProjectById(id)
        .then((res) => {
          if (res.data.code !== 0 || !res.data.data) {
            this.$message.error((res.data && res.data.message) || '加载失败')
            return
          }
          const p = res.data.data
          this.projectId = p.id
          this.title = p.title || ''
          try {
            this.pages = JSON.parse(p.pagesJson || '[]')
          } catch (err) {
            this.pages = []
          }
          try {
            const theme = JSON.parse(p.themeJson || '{}')
            if (theme.pageW) this.pageW = Number(theme.pageW)
            if (theme.pageH) this.pageH = Number(theme.pageH)
          } catch (err) {}
          this.$message.success('已加载转换结果，请逐页确认分类')
        })
        .catch((err) => this.$message.error('加载失败：' + err))
        .finally(() => {
          this.converting = false
        })
    },
    onPptxChange(e) {
      const f = e.target.files && e.target.files[0]
      if (!f) return
      if (!/\.pptx$/i.test(f.name)) {
        this.$message.warning('仅支持 .pptx 格式')
        e.target.value = ''
        return
      }
      if (f.size > 1000 * 1024 * 1024) {
        this.$message.warning('文件超过 1000MB，请拆分或压缩后再转换')
        e.target.value = ''
        return
      }
      this.file = f
      this.fileName = f.name
      if (!this.title) this.title = f.name.replace(/\.pptx$/i, '')
    },
    startConvert() {
      if (!this.file || this.converting || this.convertingQuick) return
      this.converting = true
      convertPptxSmart(this.file, this.title)
        .then((res) => {
          if (res.data.code !== 0 || !res.data.data) {
            this.$message.error((res.data && res.data.message) || '转换失败')
            return
          }
          const p = res.data.data
          this.projectId = p.id
          try {
            this.pages = JSON.parse(p.pagesJson || '[]')
          } catch (err) {
            this.pages = []
          }
          try {
            const theme = JSON.parse(p.themeJson || '{}')
            if (theme.pageW) this.pageW = Number(theme.pageW)
            if (theme.pageH) this.pageH = Number(theme.pageH)
          } catch (err) {}
          if (!this.pages.length) {
            this.$message.warning('转换结果为空')
            this.reset()
            return
          }
          this.$message.success('转换完成，请逐页确认分类结果')
        })
        .catch((err) => this.$message.error('转换失败：' + err))
        .finally(() => {
          this.converting = false
        })
    },
    // 快速转换：仅整页渲染（无动画截帧/视频分析），适合无动画 PPT
    // 与复杂转换一致落在逐页分类结果页，便于逐页检查/替换图片后再保存
    startQuickConvert() {
      if (!this.file || this.converting || this.convertingQuick) return
      this.convertingQuick = true
      convertPptx(this.file, this.title)
        .then((res) => {
          if (res.data.code !== 0 || !res.data.data) {
            this.$message.error((res.data && res.data.message) || '快速转换失败')
            return
          }
          const p = res.data.data
          this.projectId = p.id
          try {
            this.pages = JSON.parse(p.pagesJson || '[]')
          } catch (err) {
            this.pages = []
          }
          try {
            const theme = JSON.parse(p.themeJson || '{}')
            if (theme.pageW) this.pageW = Number(theme.pageW)
            if (theme.pageH) this.pageH = Number(theme.pageH)
          } catch (err) {}
          if (!this.pages.length) {
            this.$message.warning('转换结果为空')
            this.reset()
            return
          }
          this.$message.success('快速转换完成，可逐页检查后保存')
        })
        .catch((err) => this.$message.error('快速转换失败：' + err))
        .finally(() => {
          this.convertingQuick = false
        })
    },
    // 动画预览：打开前重置到初始帧
    previewAnim(row) {
      this.animPreviewRow = row
      this.animStep = 0
      this.animVisible = true
    },
    animPrev() {
      if (this.animStep > 0) this.animStep--
    },
    animNext() {
      if (this.animStep < this.animTotal) this.animStep++
      if (this.animStep >= this.animTotal) this.stopAnimAuto()
    },
    toggleAnimAuto() {
      if (this.animPlaying) {
        this.stopAnimAuto()
        return
      }
      if (this.animStep >= this.animTotal) this.animStep = 0
      this.animPlaying = true
      this.animTimer = setInterval(() => {
        if (this.animStep >= this.animTotal) {
          this.stopAnimAuto()
          return
        }
        this.animStep++
      }, 900)
    },
    stopAnimAuto() {
      this.animPlaying = false
      if (this.animTimer) {
        clearInterval(this.animTimer)
        this.animTimer = null
      }
    },
    openVideoUpload(page) {
      this.uploadTarget = page
      this.$refs.videoInput.value = ''
      this.$refs.videoInput.click()
    },
    onVideoChange(e) {
      const f = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!f) return
      if (!/\.mp4$/i.test(f.name)) {
        this.$message.warning('仅支持 mp4 格式')
        return
      }
      const page = this.uploadTarget
      if (!page) return
      this.uploadingIdx = page.id
      uploadPptVideo(f)
        .then((res) => {
          const data = res.data || {}
          if (data.code !== 0 || !data.data) {
            this.$message.error(data.message || '视频上传失败')
            return
          }
          // 升级为 video 页：整页图作封面，全页视频播放
          page.type = 'video'
          page.videoUrl = data.data
          delete page.videoBox
          this.$message.success('第 ' + page.id + ' 页视频已补录')
        })
        .catch((err) => this.$message.error('视频上传失败：' + err))
        .finally(() => {
          this.uploadingIdx = null
        })
    },
    // 普通页替换图片：上传后即用新图（保存后写库生效）
    openImgUpload(page) {
      this.imgTarget = page
      this.$refs.imgInput.value = ''
      this.$refs.imgInput.click()
    },
    onImgChange(e) {
      const f = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!f) return
      if (!/\.(jpe?g|png)$/i.test(f.name)) {
        this.$message.warning('仅支持 jpg / png 图片')
        return
      }
      const page = this.imgTarget
      if (!page) return
      this.imgUploadingId = page.id
      uploadPptImage(f)
        .then((res) => {
          const data = res.data || {}
          if (data.code !== 0 || !data.data) {
            this.$message.error(data.message || '图片上传失败')
            return
          }
          page.img = data.data
          this.$message.success('第 ' + page.id + ' 页图片已替换，点击「保存」生效')
        })
        .catch((err) => this.$message.error('图片上传失败：' + err))
        .finally(() => {
          this.imgUploadingId = null
        })
    },
    clearPageVideo(page) {
      page.type = 'image'
      delete page.videoUrl
      delete page.videoBox
      this.$message.success('第 ' + page.id + ' 页已恢复为静态整页图')
    },
    // 删除本页（如空白页/多余页），点「保存」后生效
    delPage(index) {
      const p = this.pages[index]
      if (!p) return
      this.$confirm('确认删除第 ' + (index + 1) + ' 页（页号 ' + p.id + '）？删除后点「保存」生效。', '删除本页', { type: 'warning' })
        .then(() => {
          this.pages.splice(index, 1)
          this.$message.success('已删除本页，点击「保存」生效')
        })
        .catch(() => {})
    },
    save() {
      this.saving = true
      updatePptProject({
        id: Number(this.projectId),
        title: this.title,
        themeJson: JSON.stringify({ pageW: this.pageW, pageH: this.pageH }),
        pagesJson: JSON.stringify(this.pages)
      })
        .then((res) => {
          if (res.data.code !== 0) {
            this.$message.error((res.data && res.data.message) || '保存失败')
            return
          }
          this.$message.success('已保存，进入预览检查')
          this.$router.push('/pptpreview?id=' + this.projectId)
        })
        .catch((err) => this.$message.error('保存失败：' + err))
        .finally(() => {
          this.saving = false
        })
    },
    reset() {
      this.projectId = null
      this.pages = []
      this.file = null
      this.fileName = ''
      // 清除 ?id= 参数，避免刷新后重新加载旧结果
      if (this.$route.query && this.$route.query.id) {
        this.$router.replace('/pptconvert')
      }
    },
    fmtVideo(url) {
      if (!url) return ''
      try {
        const u = url.split('?')[0]
        return u.substring(u.lastIndexOf('/') + 1).slice(0, 24)
      } catch (e) {
        return ''
      }
    }
  }
}
</script>

<style scoped>
.pptconvert {
  padding: 16px;
  background: #f5f6f8;
  min-height: 100vh;
  box-sizing: border-box;
}
.topbar {
  display: flex; align-items: center; background: #fff;
  border-radius: 6px; padding: 10px 16px; margin-bottom: 14px;
}
.page-title { font-size: 16px; font-weight: 600; margin-left: 12px; }
.tip { margin-left: 16px; font-size: 12px; color: #999; }
.panel {
  background: #fff; border-radius: 6px; padding: 20px; margin-bottom: 14px;
}
.panel-title { font-size: 14px; font-weight: 600; margin-bottom: 16px; }
.upload-row { display: flex; align-items: center; margin-bottom: 12px; }
.build-info { font-size: 12px; color: #909399; margin-top: 4px; }
.file-name { margin-left: 12px; color: #666; font-size: 13px; }
.converting { display: flex; align-items: center; gap: 10px; color: #606266; }
.stat-row { display: flex; gap: 16px; margin-bottom: 16px; }
.stat {
  display: flex; align-items: baseline; gap: 8px;
  background: #f5f7fa; border-radius: 6px; padding: 10px 18px;
}
.stat .num { font-size: 22px; font-weight: 700; color: #606266; }
.stat.warn .num { color: #e6a23c; }
.stat.ok .num { color: #409eff; }
.stat .label { font-size: 12px; color: #909399; }
.thumb { width: 120px; height: 68px; object-fit: contain; background: #f0f2f5; border-radius: 3px; }
.save-row { margin-top: 16px; display: flex; align-items: center; }
.save-tip { margin-left: 12px; font-size: 12px; color: #999; }
/* 动画预览弹窗（与 ppedit 一致） */
.anim-pv {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.anim-stage {
  background: #141824;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 360px;
  padding: 14px;
}
.anim-stage img {
  max-width: 100%;
  max-height: 62vh;
  object-fit: contain;
  background: #fff;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.35);
}
.anim-ctrl {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}
.anim-progress {
  font-size: 13px;
  color: #666;
  max-width: 560px;
  line-height: 1.6;
}
</style>